import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, Clock, X, ExternalLink, Repeat } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import useDraggable from '../hooks/useDraggable';

const POLL_MS = 10 * 60 * 1000; // 10 min

// Surface pending actions for the logged-in agent on login + as a periodic
// floating bell when there are unresolved items. Mirrors the hours-review
// daily-modal pattern but data-driven from the agent_actions table.
export default function AgentActionsModal() {
  const { t } = useTranslation();
  const { user, isClient, isImpersonating } = useAuth();
  const navigate = useNavigate();

  const [actions, setActions] = useState([]);
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);
  // Arrastrable y en el dock como las demás burbujas: antes era un fixed suelto en
  // bottom-left que se superponía con el timer de proyecto y el "ver como".
  const { pos: dragPos, isDragging, wasDragged, handlers: dragHandlers, elementRef: bellRef } =
    useDraggable('agent-actions-bell', { defaultZone: 'bottom-left', width: 48, height: 48 });

  const fetchMine = useCallback(async () => {
    try {
      const r = await api.get('/agent-actions/mine');
      setActions(r.data?.actions || []);
    } catch {
      setActions([]);
    }
  }, []);

  // Fetch on login + poll
  useEffect(() => {
    if (!user || isClient || isImpersonating) {
      setActions([]);
      return undefined;
    }
    fetchMine();
    const interval = setInterval(fetchMine, POLL_MS);
    const onFocus = () => fetchMine();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [user, isClient, isImpersonating, fetchMine]);

  // Auto-open modal once per DAY when actions exist (login nag).
  // Was once per sessionStorage session — an agent who never closes the tab
  // saw it exactly once ever. localStorage keyed by date guarantees a daily hit.
  useEffect(() => {
    if (!user || actions.length === 0) return;
    const today = new Date().toISOString().slice(0, 10);
    const dayKey = `agentActionsShown:${user.id}:${today}`;
    if (localStorage.getItem(dayKey)) return;
    localStorage.setItem(dayKey, '1');
    setOpen(true);
  }, [user, actions.length]);

  // Auto-close if there's nothing to show
  useEffect(() => {
    if (open && actions.length === 0) setOpen(false);
  }, [open, actions.length]);

  if (!user || isClient || isImpersonating) return null;
  if (actions.length === 0 && !open) {
    // Hide entirely when nothing to nag about
    return null;
  }

  const complete = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/agent-actions/${id}/complete`);
      toast.success(t('agentActions.markedDone'));
      fetchMine();
    } catch (err) {
      // Si la tarea ya no está pendiente (la cancelaron o la completaron desde otro lado
      // mientras el modal la mostraba), decirlo con todas las letras y refrescar la lista
      // para que la tarea fantasma desaparezca. Con el error genérico, el agente reintenta
      // una y otra vez creyendo que la falla es suya.
      const code = err?.response?.data?.code;
      if (code === 'NOT_PENDING' || code === 'NOT_FOUND') {
        toast(t('agentActions.noLongerPending'), { icon: 'ℹ️' });
        fetchMine();
      } else {
        toast.error(t('common.error'));
      }
    } finally {
      setBusyId(null);
    }
  };

  const snooze = async (id, hours) => {
    setBusyId(id);
    try {
      await api.post(`/agent-actions/${id}/snooze`, { hours });
      toast.success(t('agentActions.snoozed'));
      fetchMine();
    } catch {
      toast.error(t('common.error'));
    } finally {
      setBusyId(null);
    }
  };

  const goLink = (action) => {
    if (!action.link) return;
    if (action.link.startsWith('http://') || action.link.startsWith('https://')) {
      window.open(action.link, '_blank', 'noopener');
    } else {
      navigate(action.link);
      setOpen(false);
    }
  };

  const highCount = actions.filter(a => a.priority === 'high').length;

  // Floating bell button — visible when actions exist but modal closed
  const bell = !open && actions.length > 0 && (
    <button ref={bellRef} {...dragHandlers}
      onClick={() => { if (!wasDragged.current) setOpen(true); }}
      title={t('agentActions.bellTitle', { count: actions.length })}
      style={{
        // La posición la da el dock (hooks/useDraggable), que ya descuenta la barra
        // inferior del celular y la barra lateral del escritorio.
        position: 'fixed', left: dragPos.x, top: dragPos.y, zIndex: 999,
        cursor: isDragging ? 'grabbing' : 'pointer',
        transition: isDragging ? 'none' : 'left 0.18s ease, top 0.18s ease',
        width: 48, height: 48, borderRadius: '50%',
        background: highCount > 0 ? 'var(--c-open)' : 'var(--c-brand)',
        color: 'white', border: 'none', cursor: 'pointer',
        boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
      <Bell size={20} />
      <span style={{
        position: 'absolute', top: -4, right: -4,
        background: 'var(--c-surface)', color: 'var(--c-text)',
        borderRadius: '50%', width: 22, height: 22,
        fontSize: 11, fontWeight: 700,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: '2px solid var(--c-border)',
      }}>{actions.length}</span>
    </button>
  );

  const modal = open && (
    <div onMouseDown={(e) => { if (e.target === e.currentTarget) { (() => setOpen(false))(e); } }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.5)', padding: 16,
      }}>
      <div onClick={e => e.stopPropagation()} className="card"
        style={{ margin: 0, maxWidth: 600, width: '100%', maxHeight: '85vh', overflowY: 'auto' }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Bell size={18} /> {t('agentActions.modalTitle', { count: actions.length })}
          </div>
          <button className="btn btn-ghost btn-icon" onClick={() => setOpen(false)}><X size={16} /></button>
        </div>

        {actions.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--c-text-muted)' }}>
            {t('agentActions.allCaughtUp')} 🎉
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {actions.map(a => (
              <ActionCard
                key={a.id}
                action={a}
                busy={busyId === a.id}
                onComplete={() => complete(a.id)}
                onSnooze={(h) => snooze(a.id, h)}
                onGo={() => goLink(a)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(
    <>{bell}{modal}</>,
    document.body
  );
}

function ActionCard({ action, busy, onComplete, onSnooze, onGo }) {
  const { t } = useTranslation();
  const [showSnoozeMenu, setShowSnoozeMenu] = useState(false);
  const priColor = action.priority === 'high' ? 'var(--c-open)'
                 : action.priority === 'low' ? 'var(--c-text-muted)'
                 : 'var(--c-pending)';

  return (
    <div style={{
      border: '1px solid var(--c-border)', borderLeft: `4px solid ${priColor}`,
      borderRadius: 6, padding: 12, background: 'var(--c-surface-2)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 14 }}>{action.title}</div>
          {action.description && (
            <div style={{ fontSize: 13, color: 'var(--c-text-muted)', marginTop: 4 }}>
              {action.description}
            </div>
          )}
          {action.due_date && (
            <div style={{ fontSize: 11, color: 'var(--c-text-muted)', marginTop: 6 }}>
              {t('agentActions.due')}: {action.due_date}
            </div>
          )}
          <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
            {action.is_recurring && (
              <span style={{ fontSize: 11, color: 'var(--c-brand)', display: 'flex', alignItems: 'center', gap: 3 }}>
                <Repeat size={10} /> {t('agentActions.recurringTag')}
              </span>
            )}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 10, position: 'relative' }}>
        {action.link && (
          <button className="btn btn-ghost btn-sm" onClick={onGo} disabled={busy}>
            <ExternalLink size={12} /> {t('agentActions.go')}
          </button>
        )}
        <button className="btn btn-ghost btn-sm" onClick={() => setShowSnoozeMenu(s => !s)} disabled={busy}>
          <Clock size={12} /> {t('agentActions.snooze')}
        </button>
        {showSnoozeMenu && (
          <div style={{
            position: 'absolute', top: '100%', right: 80, marginTop: 4,
            background: 'var(--c-surface)', border: '1px solid var(--c-border)',
            borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 10, minWidth: 140,
          }}>
            {[1, 4, 24, 72].map(h => (
              <button key={h} className="btn btn-ghost"
                style={{ width: '100%', justifyContent: 'flex-start', padding: '6px 12px' }}
                onClick={() => { setShowSnoozeMenu(false); onSnooze(h); }}>
                {h === 1 ? t('agentActions.snooze1h')
                  : h === 4 ? t('agentActions.snooze4h')
                  : h === 24 ? t('agentActions.snooze1d')
                  : t('agentActions.snooze3d')}
              </button>
            ))}
          </div>
        )}
        <button className="btn btn-success btn-sm" onClick={onComplete} disabled={busy}>
          <Check size={12} /> {t('agentActions.done')}
        </button>
      </div>
    </div>
  );
}
